import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1095Codes = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="ACA Form 1095-C Codes Explained: Line 14,15 & 16 | TaxZerone"
          description="Learn how to report ACA Form 1095-C codes for Lines 14, 15, and 16, avoid IRS penalties, and e-file Form 1095-C accurately and confidently with TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-1095-c-codes/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">ACA Forms Codes</span>
          </span>{' '}
          for Form 1095-C : Line 14, Line 15 & Line 16 Explained
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Understand each ACA code, avoid filing errors, and stay IRS-compliant.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=acacodes`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1095-c-codes/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    ACA Form 1095-C Codes at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        {'Form '}
                        <strong style={{ fontWeight: 600 }}>
                          1095-C codes
                        </strong>
                        {
                          ' help employers report health coverage offers, employee contributions, and ACA compliance details to the IRS.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Line 14 Codes:
                        </strong>
                        {
                          ' Explain the type of health coverage offered to employees, spouses, and dependents, including whether the coverage meets '
                        }
                        ACA
                        {
                          ' minimum essential coverage (MEC) and minimum value requirements.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Line 15:</strong>
                        {
                          ' Reports the employee’s required monthly contribution for the lowest-cost, self-only coverage that provides minimum value.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Line 16 Codes:
                        </strong>
                        {
                          ' Indicate safe harbors, coverage status, or other reasons an employer may not be subject to ACA Employer Shared Responsibility penalties.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Line 17:</strong>
                        {
                          ' Reports the ZIP code used to determine affordability for Individual Coverage HRA (ICHRA) reporting.'
                        }
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#0220ff' }}
                  >
                    {' '}
                    Form 1095-C
                  </Link>{' '}
                  is used by{' '}
                  <span style={{ fontWeight: 600 }}>
                    Applicable Large Employers (ALEs)
                  </span>{' '}
                  to report health coverage offers made to full-time employees
                  under the Affordable Care Act (ACA). A critical part of this
                  form is the use of{' '}
                  <span style={{ fontWeight: 600 }}>
                    IRS-defined codes on Line 14, Line 15, and Line 16.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This guide breaks down{' '}
                  <span style={{ fontWeight: 600 }}>
                    ACA Form 1095-C codes,
                  </span>{' '}
                  explaining what each code means, when to use it, and how to
                  avoid common reporting mistakes—so you can file accurately and
                  stay{' '}
                  <span style={{ fontWeight: 600 }}>
                    IRS-compliant with confidence.
                  </span>
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is ACA Form 1095-C Codes?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#under" sx={{ color: '#0202ff' }}>
                          Understanding Lines 14, 15 and 16
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#line14" sx={{ color: '#0202ff' }}>
                          Line 14 Codes – Coverage Offer Details
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#line15" sx={{ color: '#0202ff' }}>
                          Line 15 – Employee Cost of Coverage
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#line16" sx={{ color: '#0202ff' }}>
                          Line 16 Codes – Safe Harbors & Penalty Relief
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#mistake" sx={{ color: '#0202ff' }}>
                          Common Form 1095-C Coding Mistakes
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Employer Scenarios (How to Code them on 1095 C
                          form)
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How TaxZerone Simplifies ACA Code Reporting?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What is ACA Form 1095-C Codes?
                </Typography>

                <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                  Form 1095 C codes are{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    letter and number indicators
                  </span>{' '}
                  required by the IRS to describe health coverage offers made to
                  full-time employees. These codes are reported{' '}
                  <span style={{ fontWeight: 600 }}>monthly</span> or using the
                  “<span style={{ fontWeight: 600 }}>All 12 Months</span>”
                  option.
                </Typography>
                <Typography variant="body1">
                  The IRS uses these codes to determine:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Employer Shared Responsibility compliance </li>
                  <li>
                    Whether coverage was affordable and met ACA standards{' '}
                  </li>
                  <li>
                    Whether an employer may owe penalties under IRC Section
                    4980H
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="under"
                >
                  Understanding Lines 14, 15 and 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/resources/information-returns/form-1095-c-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1095-C form
                  </Link>{' '}
                  is essential for ACA reporting, but the IRS codes on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Lines 14, 15, and 16
                  </span>{' '}
                  are the most critical areas that determine compliance.
                </Typography>
                <Typography variant="body1">
                  Here’s what each line captures and why it matters:
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Lines
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            What it shows
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Why it’s Important
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Line 14
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            The type of health coverage offered to the employee,
                            spouse, and dependents
                          </TableCell>

                          <TableCell>
                            Shows whether coverage meets ACA minimum value and
                            affordability standards.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Line 15
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Line 15 The employee’s required monthly contribution
                            for the lowest-cost, self-only coverage that
                            provides minimum value
                          </TableCell>

                          <TableCell sx={{}}>
                            Helps the IRS determine if coverage is affordable
                            and meets ACA requirements
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Line 16
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Safe harbors or reasons why the employer may not owe
                            a penalty
                          </TableCell>

                          <TableCell sx={{}}>
                            Protects the employer from potential ACA penalties
                            under IRC Section 4980H
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                  }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: '#f8f9fa',
                          fontSize: 16,
                        }}
                      >
                        Stay IRS-compliant by filing Form 1095-C correctly and
                        completing
                      </Typography>
                      <Typography variant="body2" color="#f5efeb">
                        secure e-filing without errors or penalties!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=acacodes`}
                      >
                        E-file now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="line14"
                >
                  Line 14 Codes – Coverage Offer Details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/aca-codes-line14-img.webp" />
                  <source srcSet="/assets/images/aca-codes-line14-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-codes-line14-img.png"
                    loading="lazy"
                    alt="Form 1095-c-line 14 codes-coverage offer details"
                    width={760}
                    height={51}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 14 of{' '}
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1095 c form
                  </Link>{' '}
                  reports on the type of health coverage offered to an employee
                  for each month of the calendar year. These codes tell the IRS
                  whether coverage was offered, who it was offered to, and
                  whether the offer meets ACA minimum value standards.
                </Typography>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    mb: 4,
                    textAlign: 'left',
                  }}
                >
                  <b>Important:</b> Selecting the correct Line 14 code is
                  critical because it directly impacts Employer Shared
                  Responsibility determinations and whether ACA penalties may
                  apply.
                </Alert>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 100,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Code
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Meaning
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            When to use
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            1A
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>
                              Qualifying Offer:
                            </span>{' '}
                            Minimum essential coverage providing minimum value
                            offered to full-time employee with Employee Required
                            Contribution equal to or less than 9.5% (as
                            adjusted) of mainland single federal poverty line
                            and at least minimum essential coverage offered to
                            spouse and dependent(s)
                          </TableCell>

                          <TableCell>
                            Use when the coverage meets affordability thresholds
                            and includes all eligible family members
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1B
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Minimum essential coverage (MEC), minimum value
                            offered to employee only.
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when only employee is offered Minimum Value
                            coverage.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1C
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Minimum essential coverage, minimum value offered to
                            employee and dependents (not spouse).
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when employee and dependents are offered Minimum
                            Value coverage.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1D
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Minimum essential coverage, minimum value offered to
                            employee and spouse (not dependents)
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when employee and spouse are offered Minimum
                            Value coverage (no conditional offers)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1E
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Minimum essential coverage, minimum value offered to
                            employee, spouse, and dependents
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when all family coverage is offered with minimum
                            value.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1F
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Minimum essential coverage offered that does NOT
                            provide minimum value.
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when coverage offered doesn’t meet minimum value
                            requirements.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1G
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Offer of coverage for an individual who was not an
                            employee for any month or was not full-time for any
                            month and enrolled in self-insured coverage.
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when non-employee or non-full-time individual
                            enrolled in self-insured plan.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1H
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            No offer of coverage (employee not offered any
                            health coverage or employee offered coverage that is
                            not minimum essential coverage, which may include 1
                            or more months in which the individual was not an
                            employee)
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when no MEC was offered for the month.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1J
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Minimum essential coverage for the minimum value
                            offered to employee and dependents;{' '}
                            <i>conditional</i> offer to spouse
                          </TableCell>

                          <TableCell sx={{}}>
                            Use for conditional spouse offers
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1K
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Minimum essential coverage minimum value offered to
                            employee, spouse, and dependents, with conditional
                            offer to spouse
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when spouse coverage is conditional
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 19, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Line 14 Codes for ICHRA Reporting
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To support Individual Coverage Health Reimbursement
                  Arrangements (ICHRA) reporting, the IRS introduced new ACA
                  codes on Line 14 of form 1095-c. These codes help the IRS
                  verify whether employers are meeting ACA coverage and
                  affordability requirements when offering ICHRAs.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 19, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  What is an ICHRA?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  An ICHRA is a health reimbursement arrangement introduced in
                  2020 that allows employers of any size to reimburse employees
                  for all or part of the cost of individual health insurance
                  premiums. This approach gives employers more flexibility in
                  designing benefits while allowing employees to choose coverage
                  that best fits their needs.
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 100,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Code
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Meaning
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            When to use
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            1L
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individual coverage HRA offered to employee only
                            with affordability determined by using employee’s
                            primary residence location ZIP code.
                          </TableCell>

                          <TableCell>
                            Use for certain HRA coverage offers.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1M
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individual coverage HRA offered to employee and
                            dependent(s) (not spouse) with affordability
                            determined by using employee’s primary residence
                            location ZIP code.
                          </TableCell>

                          <TableCell sx={{}}>
                            Use for HRA coverage to employee + dependents
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1N
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individual coverage HRA offered to employee, spouse,
                            and dependent(s) with affordability determined by
                            using employee’s primary residence location ZIP
                            code.
                          </TableCell>

                          <TableCell sx={{}}>
                            Use for full family HRA coverage
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1O
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individual coverage HRA offered to employees only
                            using the employee’s primary employment site ZIP
                            code affordability safe harbor.
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when affordability is based on work location
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1P
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individual coverage HRA offered to employee and
                            dependent(s) (not spouse) using the employee’s
                            primary employment site ZIP code affordability safe
                            harbor.
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when affordability is based on work location
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1Q
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individual coverage HRA offered to employee, spouse,
                            and dependent(s) using employee’s primary employment
                            site ZIP code affordability safe harbor
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when affordability is based on work location
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1R
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individual Coverage HRA offered that is NOT
                            affordable employee and spouse, or dependent(s); or
                            employee, spouse, and dependents
                          </TableCell>

                          <TableCell sx={{}}>
                            Use when HRA does not meet affordability
                            requirements
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1S
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individual coverage HRA offered to an individual who
                            was not a full-time employee.
                          </TableCell>

                          <TableCell sx={{}}>
                            Use on{' '}
                            <span style={{ fontWeight: 600 }}> Line 14</span>{' '}
                            when a full-time employee is offered an
                            employee-only ICHRA and the employer determines
                            affordability based on the{' '}
                            <span style={{ fontWeight: 600 }}>
                              worksite location
                            </span>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1T
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individual coverage HRA offered to employee and
                            spouse (not dependents) with affordability
                            determined using employee’s primary residence
                            location ZIP code
                          </TableCell>

                          <TableCell sx={{}}>
                            Use this code on{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              Line 14 of Form 1095-c
                            </span>{' '}
                            when a full-time employee is offered an ICHRA that
                            covers the employee and spouse only, and the
                            employer uses the{' '}
                            <span style={{ fontWeight: 600 }}>
                              employee’s residence location
                            </span>{' '}
                            to determine affordability for ACA reporting
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1U
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Individual coverage HRA offered to employee and
                            spouse (not dependents) using employee’s primary
                            employment site ZIP code affordability safe harbor.
                          </TableCell>

                          <TableCell sx={{}}>
                            Use this code on{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              Line 14 of Form 1095 C
                            </span>{' '}
                            when a full-time employee is offered an ICHRA
                            covering the employee and spouse only, and the
                            employer uses the{' '}
                            <span style={{ fontWeight: 600 }}>
                              worksite (employment location)
                            </span>{' '}
                            to determine affordability for ACA reporting.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="line15"
                >
                  Line 15- Employee Required Condition
                </Typography>
                <picture>
                  <source srcSet="/assets/images/aca-codes-line15-img.webp" />
                  <source srcSet="/assets/images/aca-codes-line15-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-codes-line15-img.png"
                    loading="lazy"
                    alt="Form 1095-c-line 15-employee required condition"
                    width={760}
                    height={38}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 15</span> of form 1095
                  c reports the{' '}
                  <span style={{ fontWeight: 600 }}>
                    employee’s required contribution
                  </span>{' '}
                  for the{' '}
                  <span style={{ fontWeight: 600 }}>
                    lowest-cost, self-only coverage
                  </span>{' '}
                  that meets minimum value standards.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Critical Reporting Rule
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Even if an employee enrolled in a different plan, chose family
                  coverage, or declined coverage altogether, Line 15 must always
                  reflect the self-only rate of the most affordable
                  ACA-compliant plan offered.
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            What to Report
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontSize: 16,
                              width: 300,
                              fontWeight: 600,
                            }}
                          >
                            How to Apply It
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            Dollar amount (for example, $102.34)
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Report the{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              monthly cost
                            </span>{' '}
                            of the lowest-cost self-only plan that meets minimum
                            value requirements
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontSize: 15,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Lowest-cost self-only coverage
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Do <span style={{ fontWeight: 600 }}> not</span>{' '}
                            report family, spouse, or dependent coverage costs
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontSize: 15,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Leave blank if Line 14 = 1A or 1G
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            If Line 14 uses code{' '}
                            <span style={{ fontWeight: 600 }}> 1A</span>{' '}
                            (qualifying offer) or{' '}
                            <span style={{ fontWeight: 600 }}>1G,</span> Line 15
                            should be left blank
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontSize: 15,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Monthly amount only
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Always report a{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              monthly premium
                            </span>
                            , not an annual total. If the employee’s
                            contribution is zero, enter{' '}
                            <span style={{ fontWeight: 600 }}>0.00.</span>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontSize: 15,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Monthly or All 12 Months
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Enter amounts by month or use the{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              “All 12 Months”
                            </span>{' '}
                            row if the cost stayed the same all year. If the
                            amount changed, enter the correct premium for each
                            applicable month.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Line 15 works together with{' '}
                  <span style={{ fontWeight: 600 }}>Line 14 and Line 16</span>{' '}
                  to determine whether an employer may be subject to{' '}
                  <span style={{ fontWeight: 600 }}>
                    ACA penalties under IRC Section 4980H.
                  </span>{' '}
                  Accurate reporting helps demonstrate affordability and
                  prevents IRS penalty notices or correction requests.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="line16"
                >
                  Line 16 Codes- Safe Harbor & Other Relief for ALE Members
                </Typography>
                <picture>
                  <source srcSet="/assets/images/aca-codes-line16-img.webp" />
                  <source srcSet="/assets/images/aca-codes-line16-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-codes-line16-img.png"
                    loading="lazy"
                    alt="Form 1095-c-line 16-safe habor & other relief ale members"
                    width={760}
                    height={38}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 16 explains{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    why an employer may not owe an ACA penalty
                  </span>{' '}
                  for a specific month. Enter{' '}
                  <span style={{ fontWeight: 600 }}>
                    one applicable Code Series 2 code per month.
                  </span>{' '}
                  If the same code applies all year, you can use the{' '}
                  <span style={{ fontWeight: 600 }}>“All 12 Months”</span> box.
                  If no code applies, leave the month blank.
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 100,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Code
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Meaning
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            When to use
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                              fontSize: 15,
                            }}
                          >
                            2A
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            Employee not employed during the month{' '}
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Use for months the employee{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              was not employed
                            </span>{' '}
                            (new hire before start date, terminated employee, or
                            not employed at any time during the month)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            2B
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            Employee not a full-time employee
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Use when the employee{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              did not average 30 hours per week
                            </span>{' '}
                            or was in a{' '}
                            <span style={{ fontWeight: 600 }}>
                              limited non-assessment period
                            </span>{' '}
                            (such as a waiting period)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            2C
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            Employee enrolled in health coverage offered
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Use when the employee{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              enrolled in the employer’s coverage,
                            </span>{' '}
                            regardless of affordability
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            2D
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            Employee in a section 4980H(b) Limited
                            Non-Assessment Period
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Use during{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              waiting periods, initial measurement periods, or
                              first month of employment
                            </span>{' '}
                            when the employee isn’t yet required to be offered
                            coverage.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            2E
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            Multiemployer interim rule relief.
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Use if coverage is offered through a{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              multiemployer plan,
                            </span>{' '}
                            and the employer qualifies for relief.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            2F
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            Section 4980H affordability{' '}
                            <Link
                              href="/resources/information-returns/form-w-2-instructions/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              W2 form
                            </Link>{' '}
                            safe harbor
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Use when coverage is considered{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              affordable based on the employees{' '}
                              <Link
                                href="/resources/information-returns/form-w-2-instructions/"
                                sx={{ color: '#0202ff' }}
                              >
                                {' '}
                                W2 wages
                              </Link>
                              .
                            </span>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            2G
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            Section 4980H affordability federal poverty line
                            safe harbor.
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Use when affordability is based on the{' '}
                            <span style={{ fontWeight: 600 }}>
                              Federal Poverty Line
                            </span>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            2H
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontSize: 15,
                            }}
                          >
                            Section 4980H affordability rate of pay safe harbor.
                          </TableCell>

                          <TableCell sx={{ fontSize: 15 }}>
                            Use when affordability is calculated using the
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              employee’s rate of pay
                            </span>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                    mt: 2,
                  }}
                >
                  Line 17- ZIP Code used for ICHRA Affordability
                </Typography>
                <picture>
                  <source srcSet="/assets/images/aca-codes-line17-img.webp" />
                  <source srcSet="/assets/images/aca-codes-line17-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/aca-codes-line17-img.png"
                    loading="lazy"
                    alt="Form 1095-c-line 17-zip code used for ichra affordability"
                    width={760}
                    height={39}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 17 on 1095-C Form is used{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    only for Individual Coverage HRA (ICHRA) reporting
                  </span>
                  . When an Applicable Large Employer offers an ICHRA and
                  reports that offer using ICHRA-specific codes on Line 14 (such
                  as 1L–1U), Line 17 requires the employer to enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    ZIP code that was used to determine affordability.
                  </span>
                </Typography>
                <Box
                  component="div"
                  sx={{ backgroundColor: '#bde0fe', borderRadius: 3, p: 3 }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    ZIP Code Requirements
                  </Typography>
                  <Typography variant="body1">
                    Line 17 reports the ZIP code used to determine ICHRA
                    affordability. This ZIP code identifies the lowest-cost
                    silver plan in the Health Insurance Marketplace and supports
                    the employee's required contribution shown on Line 15.
                    Depending on the Line 14 code, the ZIP code will be either
                    the employee's home ZIP code or the primary work location
                    ZIP code if the employer uses the work location safe harbor.
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  ðŸ‘‰To know more visit our{' '}
                  <Link
                    href="/resources/information-returns/form-1095-c-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1095-C form instructions
                  </Link>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                    mt: 2,
                  }}
                  id="mistake"
                >
                  Common Form 1095-C Coding Mistakes
                </Typography>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#d5f0f6',
                    borderRadius: 3,
                    p: 3,
                    mb: 3,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Avoid These Common Errors
                  </Typography>
                  <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.5 } }}>
                    <li>
                      Using an{' '}
                      <span style={{ fontWeight: 600 }}>
                        incorrect Line 14 code,
                      </span>{' '}
                      such as reporting{' '}
                      <span style={{ fontWeight: 600 }}>
                        Code 1A for an ICHRA offer
                      </span>{' '}
                      instead of the required ICHRA-specific codes.
                    </li>
                    <li>
                      Entering an amount on{' '}
                      <span style={{ fontWeight: 600 }}>
                        Line 15 when it should be left blank,
                      </span>{' '}
                      especially when Line 14 is coded as{' '}
                      <span style={{ fontWeight: 600 }}>1H or 1A</span>.{' '}
                    </li>
                    <li>
                      Reporting the{' '}
                      <span style={{ fontWeight: 600 }}>
                        wrong premium on Line 15,
                      </span>{' '}
                      including family coverage costs or an annual total instead
                      of the
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        lowest-cost self-only monthly premium.
                      </span>{' '}
                    </li>
                    <li>
                      Selecting{' '}
                      <span style={{ fontWeight: 600 }}>
                        Line 14 and Line 16 codes that don’t align,
                      </span>{' '}
                      which can cause affordability and penalty determination
                      issues.
                    </li>
                    <li>
                      Entering the{' '}
                      <span style={{ fontWeight: 600 }}>
                        wrong ZIP code on Line 17
                      </span>{' '}
                      for ICHRA reporting by mixing up the employee’s{' '}
                      <span style={{ fontWeight: 600 }}>home ZIP code</span> and{' '}
                      <span style={{ fontWeight: 600 }}>
                        work location ZIP code
                      </span>
                      .{' '}
                    </li>
                    <li>
                      Using the{' '}
                      <span style={{ fontWeight: 600 }}>
                        same code for all 12 months
                      </span>{' '}
                      if the employee's status or coverage changes at any time
                      during the year.
                    </li>
                    <li>
                      Failure to update codes due to{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        mid-year hires, terminations, or changes in coverage
                      </span>{' '}
                      that result in incorrect monthly reporting.
                    </li>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                    mt: 2,
                  }}
                  id="common"
                >
                  Common Employer Scenarios (How to Code them on 1095-c form)
                </Typography>
                <Typography variant="body1">
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Filing Form 1095-C
                  </Link>{' '}
                  correctly depends on the employee’s coverage situation. Here
                  are common scenarios and the appropriate Line 14–16 codes to
                  use:
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                              fontSize: 15,
                            }}
                          >
                            Scenario
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                              fontSize: 15,
                              textAlign: 'center',
                            }}
                          >
                            Line 14 Code
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                              fontSize: 15,
                              textAlign: 'center',
                            }}
                          >
                            Line 15
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontSize: 15,
                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            Line 16 Code
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Employee offered minimum value coverage for self,
                            spouse, and dependents
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            1E{' '}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            Dollar amount (e.g., $135.00)
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center' }}>
                            2F, 2G or 2H
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Employee offered coverage for self only
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            1B
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            Enter monthly self-only premium
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center' }}>
                            2F, 2G or 2H
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Employee not offered any coverage
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            1H
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            Leave blank
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            2A or 2B
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Employee offered ICHRA for self and spouse (home ZIP
                            affordability)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            1T
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            Dollar amount based on Zip-based calculations
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            2F, 2G or 2H
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Employee offered ICHRA for self and spouse (worksite
                            ZIP affordability)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            1U
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            Dollar amount based on worksite ZIP
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            2F, 2G or 2H
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Employee enrolled in employer coverage
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            1C / 1D / 1E
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            Enter monthly self-only premium
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>2C</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Employee enrolled in ICHRA
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            1L, 1M, 1N, 1O, 1P, 1Q, 1S, 1T, or 1U
                            (ICHRA-specific codes) *
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            Required
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            2C (if enrolled) or 2H (safe harbor)
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Employee in waiting period or not full-time
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            1A, 1G and 1H
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'center',
                            }}
                          >
                            Leave 15 blank
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            2D or 2B
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mt: 3,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> Codes 1L–1U require Line 17 to report the ZIP
                  Code used for ICHRA affordability
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                    mt: 2,
                  }}
                  id="how"
                >
                  How TaxZerone Simplifies ACA Code Reporting?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  TaxZerone's ACA filing solution is designed to take the
                  confusion out of ACA code reporting and help employers stay
                  compliant with IRS requirements. It does this by
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li style={{}}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      IRS-compliant code guidance:
                    </span>{' '}
                    TaxZerone gives IRS-compliant code guidance that aids users
                    in selecting the right ACA codes by analyzing their coverage
                    details and employee information.
                  </li>
                  <li style={{}}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Flagging potential errors before filing:
                    </span>{' '}
                    The system identifies all missing, invalid and conflicting
                    codes through its built-in checks which enable users to
                    correct problems before they submit their work to the IRS
                    thus preventing approval problems
                  </li>
                  <li style={{}}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Generating employee copies accurately:
                    </span>{' '}
                    The system gives correct employee statements through its
                    automated process, which ensures that all necessary details
                    get reported.
                  </li>
                  <li style={{}}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Ensuring secure electronic submission:
                    </span>{' '}
                    The TaxZerone system enables secure electronic filing
                    through its e-filing process which protects sensitive
                    information while creating confirmation documents that show
                    when the IRS filing ends.
                  </li>
                  <li style={{}}>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Delivered by ZeroneVault:
                    </span>{' '}
                    Employee copies of{' '}
                    <Link
                      href="/resources/information-returns/form-1095-c-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      1095 C form
                    </Link>{' '}
                    are securely delivered through{' '}
                    <span style={{ fontWeight: 600 }}> ZeroneVault,</span>{' '}
                    allowing employees to access their forms online anytime.
                    Employers also have the option to send employee copies via
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      postal mailing,
                    </span>{' '}
                    ensuring timely and compliant distribution.
                  </li>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on the IRS
                        Guidelines. Please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i109495c.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          IRS instructions of Form 1095-C
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Simplify your ACA reporting with TaxZerone!
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              File Form 1095-C accurately, avoid IRS errors, and submit with
              confidence today
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=acacodes`}
              >
                E-file now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1095Codes;
